<template>
  <cc-compendium-browser
    :items="availableWeapons"
    item-type="MechWeapon"
    :table-headers="headers"
    :options="options"
    equippable
    @select="stageSelect($event)"
    @equip="handleEquip($event)"
  >
    <template #header>
      <div class="heading h3 text-center text-accent">Mech Weapons</div>
    </template>
    <template #top>
      <v-row>
        <v-col>
          <div v-if="weaponSlot.Weapon">
            <div
              v-if="!mobile"
              class="text-cc-overline"
            >
              UNION ARMORY PRINTID: {{ fID('ANN-NNN-NNN::AA//AA') }}
            </div>
            <div class="heading h2 text-accent">
              {{ weaponSlot.Weapon.Name }}
              <span
                v-if="selected"
                class="text-success"
              >
                <v-icon
                  icon="mdi-chevron-triple-right"
                  class="pb-1"
                />
                {{ selected.Name }}
              </span>
            </div>
            <div
              v-if="!selected"
              class="flavor-text text-cc-overline"
            >
              CURRENTLY EQUIPPED
            </div>
            <div
              v-else
              class="flavor-text text-cc-overline"
            >
              <div v-if="!eq(weaponSlot.Weapon.Range, <Range[]>selected.Range)">
                <span
                  v-html-safe="getRangeDisplay(weaponSlot.Weapon)"
                  class="text-accent"
                />
                <v-icon
                  color="success"
                  size="small"
                  icon="mdi-chevron-triple-right"
                  class="mx-1"
                />
                <span
                  v-html-safe="getRangeDisplay(<MechWeapon>selected)"
                  class="text-success"
                />
              </div>
              <div v-if="!eq(weaponSlot.Weapon.Damage, <Damage[]>selected.Damage)">
                <span
                  v-html-safe="getDamageDisplay(weaponSlot.Weapon)"
                  class="text-accent"
                />
                <v-icon
                  color="success"
                  size="small"
                  icon="mdi-chevron-triple-right"
                  class="mx-1"
                />
                <span
                  v-html-safe="getDamageDisplay(<MechWeapon>selected)"
                  class="text-success"
                />
              </div>
              <div v-if="!eq(weaponSlot.Weapon.Tags, <Tag[]>selected.Tags)">
                <span
                  v-for="t in weaponSlot.Weapon.Tags.filter(
                    x => !(<any>selected!.Tags.some(y => y.ID === x.ID))
                  )"
                  :key="`ws-tag_${t.Name}`"
                  class="text-error"
                >
                  <v-icon
                    icon="mdi-minus"
                    size="x-small"
                    class="mr-n1"
                  />
                  [{{ t.Name }}]
                </span>
                <span
                  v-for="t in selected.Tags.filter(
                    x => !weaponSlot.Weapon.Tags.some(y => y.ID === x.ID)
                  )"
                  :key="`ws-tag-b_${t.Name}`"
                  class="text-success"
                >
                  <v-icon
                    icon="mdi-plus"
                    size="x-small"
                    class="mr-n1"
                  />
                  [{{ t.Name }}]
                </span>
              </div>
            </div>
          </div>
          <div v-else-if="!mobile">
            <div class="text-cc-overline">
              UNION ARMORY EQUIPMENT AUTHORIZATION: FRAME EQUIPMENT//COMBAT SYSTEM
            </div>
            <div class="heading h2 text-disabled">NO SELECTION</div>
            <div class="flavor-text overline text-error">[ EQUIPMENT ID INVALID OR MISSING ]</div>
          </div>
        </v-col>
        <v-col
          cols="12"
          md="auto"
        >
          <div class="text-right">
            <cc-switch
              v-model="showUnlicensed"
              :label="mobile && 'Show Unlicensed'"
              color="error"
              :tooltip="
                !mobile && showUnlicensed
                  ? 'Unlicensed equipment: SHOWN'
                  : 'Unlicensed equipment: HIDDEN'
              "
              :prepend-icon="!mobile && 'cc:system'"
              on-icon="mdi-lock-open"
              off-icon="mdi-lock"
            />
            <br />
            <cc-switch
              v-model="showOverSP"
              :label="mobile && 'Show Exceeds SP'"
              color="error"
              :tooltip="
                !mobile && showOverSP
                  ? 'Systems exceeding SP Capacity: SHOWN'
                  : 'Systems exceeding SP Capacity: HIDDEN'
              "
              :prepend-icon="!mobile && 'cc:system_point'"
              on-icon="mdi-lock-open"
              off-icon="mdi-lock"
            />
          </div>
        </v-col>
      </v-row>
    </template>
  </cc-compendium-browser>
</template>

<script lang="ts">
  import * as _ from 'lodash-es'

  import { CompendiumStore } from '@/stores'
  import { Rules, MechWeapon, Mech, Range, Damage, Tag } from '@/class'
  import { useMobile } from '@/mixins/useMobile'
  import { selectorMixin } from '../../_mixins/selectorMixin'

  export default {
    name: 'WeaponSelector',
    mixins: [useMobile, selectorMixin],
    props: {
      weaponSlot: {
        type: Object,
        required: true,
      },
      mech: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      options: {
        views: ['list', 'single', 'table', 'cards', 'scatter', 'bar', 'compare'],
        initialView: 'single',
        groups: ['source', 'lcp', 'license'],
        initialGroup: 'license',
        showExotics: true,
      },
      headers: [
        { title: 'Manufacturer', align: 'left', key: 'Source' },
        { title: 'Weapon', align: 'left', key: 'Name' },
        { title: 'License', align: 'left', key: 'LicenseString' },
        { title: 'Size', align: 'left', key: 'Size' },
        { title: 'Type', align: 'left', key: 'WeaponTypes' },
        { title: 'Tags', align: 'center', key: 'Tags' },
        { title: 'Range', align: 'left', key: 'Range' },
        { title: 'Damage', align: 'left', key: 'Damage' },
      ],
      selected: null as unknown as MechWeapon | null,
    }),
    computed: {
      freeSP(): number {
        return this.weaponSlot.Weapon
          ? this.mech.FreeSP + this.weaponSlot.Weapon.SP
          : this.mech.FreeSP
      },
      availableWeapons(): MechWeapon[] {
        const fittings = Rules.MountFittings[this.weaponSlot.Size]
        let i = this.filterByLcp(CompendiumStore().MechWeapons).filter(
          x => x.Source && fittings.includes(x.Size) && !x.IsHidden && !x.IsExotic
        )

        if (this.weaponSlot.Weapon) i = i.filter(x => x.ID !== this.weaponSlot.Weapon.ID)

        if (this.isAICapacityFull()) i = i.filter(x => !x.IsAI)

        if (!this.showUnlicensed) i = i.filter(x => this.isLicensed(x))

        i = i.concat(
          this.mech.Pilot.SpecialEquipment.filter(
            x => x.ItemType === 'MechWeapon' && fittings.includes(x.Size)
          )
        )

        i = i.filter(
          x =>
            !this.mech.MechLoadoutController.ActiveLoadout.UniqueWeapons.map(y => y.ID).includes(
              x.ID
            )
        )

        return i
      },
    },
    methods: {
      handleEquip(event) {
        this.$emit('equip', event)
      },
      stageSelect(event) {
        if (event) {
          this.selected = event
        } else {
          this.selected = null
        }
      },
      getRangeDisplay(item: MechWeapon): string {
        if (!item.Range) return '---'
        const rangeStrs = [] as string[]
        item.Range.forEach(r => {
          rangeStrs.push(`${r.Type} ${r.Value}`)
        })
        return rangeStrs.join('/')
      },
      getDamageDisplay(item: MechWeapon): string {
        if (!item.Damage) return '---'
        const damageStrs = [] as string[]
        item.Damage.forEach(d => {
          damageStrs.push(`${d.Type} ${d.Value}`)
        })
        return damageStrs.join('/')
      },
      eq(a: Range[] | Damage[] | Tag[], b: Range[] | Damage[] | Tag[]): boolean {
        if (!a || !b) return false
        if (a.length !== b.length) return false

        const aIDs = a.map(x => (x as any).ID || x.Value || x.Type)
        const bIDs = b.map(x => (x as any).ID || x.Value || x.Type)

        return _.isEqual(_.sortBy(aIDs), _.sortBy(bIDs))
      },
    },
  }
</script>
